import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface ArchivedPricingTableVersion {
  id: string;
  pricing_table_id: string;
  version_number: number;
  data: any;
  created_at: Date;
}

interface EditVersionModalProps {
  version: ArchivedPricingTableVersion | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVersionUpdated: () => void;
}

export function EditVersionModal({ version, isOpen, onOpenChange, onVersionUpdated }: EditVersionModalProps) {
  const [versionName, setVersionName] = useState('');
  const [price, setPrice] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (version && version.data) {
      const data = version.data as any;
      setVersionName(data.version_name || '');
      setPrice(data.price?.toString() || '');
      setProductDescription(data.product_description || '');
      setFormError(null);
    } else {
      setVersionName('');
      setPrice('');
      setProductDescription('');
    }
  }, [version]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!version) return;
    
    setIsLoading(true);
    setFormError(null);

    try {
      const response = await fetch(`/api/pricing-tables/versions/${version.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          versionName,
          price: parseFloat(price),
          productDescription,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update version');
      }

      const result = await response.json();

      if (result.success) {
        onVersionUpdated();
        onOpenChange(false);
        alert(`Version '${versionName}' updated successfully.`);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update version. Please try again.';
      setFormError(errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Version</DialogTitle>
          <DialogDescription>
            Make changes to the pricing table version here. Click save when you're done.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor='edit-versionName'>Version Name</Label>
            <Input
              id='edit-versionName'
              value={versionName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVersionName(e.target.value)}
              maxLength={100}
              disabled={isLoading}
              placeholder="e.g., Basic Plan"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor='edit-price'>Price ($)</Label>
            <Input
              type='number'
              id='edit-price'
              value={price}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrice(e.target.value)}
              step='0.01'
              min='0'
              disabled={isLoading}
              placeholder="e.g., 9.99"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor='edit-productDescription'>Product Description</Label>
            <Textarea
              id='edit-productDescription'
              value={productDescription}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setProductDescription(e.target.value)}
              maxLength={500}
              disabled={isLoading}
              placeholder="Describe the features included in this version..."
              rows={4}
              required
            />
          </div>
          {formError && <p className="text-sm text-red-600">Error: {formError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={isLoading} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !version}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
